<template>
    <div class="flights-list-container">
        <!-- Header -->
        <div class="airports-header card-layout" v-if="originAirports.length > 0 && destinationAirports.length > 0">
            <!-- Origin airports -->
            <div class="header-item origin">
                <span class="iata-codes-container">
                    <span class="iata-code" v-for="airport in originAirports" :key="'origin_airport_' + airport.id">
                        {{ airport.iata_airport_code }}
                    </span>
                </span>

                <span class="airport-names">
                    {{ originAirports.map(airport => airport.name).join(', ') }}
                </span>
            </div>

            <!-- Icon -->
            <i class="fa fa-exchange"></i>

            <!-- Destination airports -->
            <div class="header-item destination">
                <span class="iata-codes-container">
                    <span class="iata-code" v-for="airport in destinationAirports" :key="'origin_airport_' + airport.id">
                        {{ airport.iata_airport_code }}
                    </span>
                </span>

                <span class="airport-names">
                    {{ destinationAirports.map(airport => airport.name).join(', ') }}
                </span>
            </div>
        </div>

        <!-- Filter -->
        <div class="filters-container" v-if="selectedFlight === null">
            <!-- Stops -->
			<div class="dropdown">
				<button class="btn btn-primary dropdown-toggle" type="button" data-toggle="dropdown" :class="{ active: filters.max_stops !== null }">
                    <span v-if="filters.max_stops === null">
                        {{ 'checkout.steps.flights.filters.stops.label' | trans }}
                    </span>

                    <span v-if="filters.max_stops !== null">
                        {{ 'checkout.steps.flights.filters.stops.stops_' + filters.max_stops | trans }}
                    </span>
                </button>
                
                <div class="dropdown-menu">
                    <!-- Any -->
                    <a class="dropdown-item" href="#" @click.prevent="filters.max_stops = null" :class="{ active: filters.max_stops === null }">
                        <i class="far fa-check mr-1" v-if="filters.max_stops === null"></i>
                        {{ 'checkout.steps.flights.filters.stops.any' | trans }}
                    </a>

                    <!-- Direct -->
                    <a class="dropdown-item" href="#" @click.prevent="filters.max_stops = 0" :class="{ active: filters.max_stops === 0 }">
                        <i class="far fa-check mr-1" v-if="filters.max_stops === 0"></i>
                        {{ 'checkout.steps.flights.filters.stops.stops_0' | trans }}
                    </a>

                    <!-- One stop -->
                    <a class="dropdown-item" href="#" @click.prevent="filters.max_stops = 1" :class="{ active: filters.max_stops === 1 }">
                        <i class="far fa-check mr-1" v-if="filters.max_stops === 1"></i>
                        {{ 'checkout.steps.flights.filters.stops.stops_1' | trans }}
                    </a>

                    <!-- Multiple stops -->
                    <a class="dropdown-item" href="#" @click.prevent="filters.max_stops = 2" :class="{ active: filters.max_stops === 2 }">
                        <i class="far fa-check mr-1" v-if="filters.max_stops === 2"></i>
                        {{ 'checkout.steps.flights.filters.stops.stops_2' | trans }}
                    </a>
                </div>
            </div>

            <!-- Time frame -->
            <div class="dropdown">
				<button class="btn btn-primary dropdown-toggle" type="button" data-toggle="dropdown" :class="{ active: filters.outbound_time_frame !== null || filters.return_time_frame !== null }">
                    <span v-if="filters.outbound_time_frame === null && filters.return_time_frame === null">
                        {{ 'checkout.steps.flights.filters.timeFrame.label' | trans }}
                    </span>

                    <span v-if="filters.outbound_time_frame !== null">
                        {{ 'checkout.steps.flights.filters.timeFrame.' + filters.outbound_time_frame + 'Short' | trans }}
                    </span>
                    <span v-if="filters.outbound_time_frame !== null && filters.return_time_frame !== null">
                        &nbsp;|&nbsp;
                    </span>
                    <span v-if="filters.return_time_frame !== null">
                        {{ 'checkout.steps.flights.filters.timeFrame.' + filters.return_time_frame + 'Short' | trans }}
                    </span>
                </button>
                
                <div class="dropdown-menu">
                    <span class="dropdown-header">
                        {{ 'checkout.steps.flights.filters.timeFrame.outbound' | trans }}
                    </span>
                    <!-- Any -->
                    <a class="dropdown-item" href="#" @click.prevent="filters.outbound_time_frame = null" :class="{ active: filters.outbound_time_frame === null }">
                        <i class="far fa-check mr-1" v-if="filters.outbound_time_frame === null"></i>
                        {{ 'checkout.steps.flights.filters.timeFrame.any' | trans }}
                    </a>

                    <a class="dropdown-item"
                    href="#"
                    @click.prevent="filters.outbound_time_frame = timeFrame"
                    :class="{ active: filters.outbound_time_frame === timeFrame }"
                    v-for="timeFrame in possibleTimeFrames"
                    :key="'outbound_time_frame_' + timeFrame">
                        <i class="far fa-check mr-1" v-if="filters.outbound_time_frame === timeFrame"></i>
                        {{ 'checkout.steps.flights.filters.timeFrame.' + timeFrame | trans }}
                    </a>

                    <span class="dropdown-header">
                        {{ 'checkout.steps.flights.filters.timeFrame.return' | trans }}
                    </span>
                    <!-- Any -->
                    <a class="dropdown-item" href="#" @click.prevent="filters.return_time_frame = null" :class="{ active: filters.return_time_frame === null }">
                        <i class="far fa-check mr-1" v-if="filters.return_time_frame === null"></i>
                        {{ 'checkout.steps.flights.filters.timeFrame.any' | trans }}
                    </a>

                    <a class="dropdown-item"
                    href="#"
                    @click.prevent="filters.return_time_frame = timeFrame"
                    :class="{ active: filters.return_time_frame === timeFrame }"
                    v-for="timeFrame in possibleTimeFrames"
                    :key="'return_time_frame_' + timeFrame">
                        <i class="far fa-check mr-1" v-if="filters.return_time_frame === timeFrame"></i>
                        {{ 'checkout.steps.flights.filters.timeFrame.' + timeFrame | trans }}
                    </a>
                </div>
            </div>

            <!-- Airlines -->
            <div class="dropdown">
				<button class="btn btn-primary dropdown-toggle" type="button" data-toggle="dropdown" :class="{ active: filters.airlines.length > 0 }">
                    <span v-if="filters.airlines.length === 0">
                        {{ 'checkout.steps.flights.filters.airlines.label' | trans }}
                    </span>

                    <span v-if="filters.airlines.length > 0">
                        {{ 'checkout.steps.flights.filters.airlines.labelCount' | trans({ count: filters.airlines.length }) }}
                    </span>
                </button>
                
                <div class="dropdown-menu">
                    <ul>
						<li>
							<input type="checkbox" id="airlinesAll" :checked="filters.airlines.length === 0" @click="filters.airlines = []" />
							<label for="airlinesAll">
								{{ 'checkout.steps.flights.filters.airlines.all' | trans }}
							</label>
						</li>

                        <li v-for="airline in possibleAirlines" :key="'airlines_' + airline.id">
							<input type="checkbox" :id="'airline_' + airline.id" :value="airline.iata_code" v-model="filters.airlines" />
							<label :for="'airline_' + airline.id">
								{{ airline.name }}
							</label>
						</li>
                    </ul>
                </div>
            </div>
        </div>

        <h2 class="mt-4 mb-3" v-if="selectedFlight">
            {{ 'checkout.steps.flights.yourSelectedFlight' | trans }}
        </h2>

        <!-- Price changes -->
        <div class="alert alert-success" v-if="totalPriceChange.change < 0 && selectedFlight">
            Erfreulicherweise sind die Flugpreise an den von Dir gewählten Daten kürzlich <strong>gesunken</strong>.
            Der Preis Deiner Reise hat sich daher um <strong>{{ totalPriceChange.change | formatPrice }} &euro;</strong> reduziert.
        </div>
        <div class="alert alert-warning" v-if="totalPriceChange.change > 0 && selectedFlight">
            Leider sind die Flugpreise an den von Dir gewählten Daten kürzlich <strong>gestiegen</strong>.
            Der Preis Deiner Reise hat sich daher um <strong>{{ totalPriceChange.change | formatPrice }} &euro;</strong> erhöht.
        </div>

        <div class="card-layout">
            <!-- Meta container -->
            <div class="meta-container" v-if="selectedFlight === null">
                <!-- No flights found text -->
                <span class="not-found-text" v-if="loadingState.count >= loadingState.total && flights.length === 0 && loadingState.count > 0">
                    <img :src="$cdn.default + 'assets/images/checkout/airplane-cabin.svg?raw=true'" />

                    <strong>{{ 'checkout.steps.flights.noFlightsFoundFirst' | trans }}</strong>
                    {{ 'checkout.steps.flights.noFlightsFoundSecond' | trans }}
                </span>

                <!-- Results text -->
                <span class="info-text" v-if="loadingState.count > 0 && flights.length > 0" v-html="$options.filters.trans('checkout.steps.flights.foundFlights', { count: flights.length })"></span>

                <!-- Loading text -->
                <span class="info-text" v-if="loadingState.count < loadingState.total">
                    {{ 'checkout.steps.flights.lookingForFlights' | trans }}
                </span>

                <!-- Progress bar for chunks -->
                <div class="progress" v-if="loadingState.count < loadingState.total">
                    <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" :style="{ width: ((100 / loadingState.total) * loadingState.count) + '%' }"></div>
                </div>
            </div>

            <!-- Loader -->
            <div class="d-flex flex-column py-3 justify-content-center align-items-center" v-if="loadingState.count === 0">
                <svg class="loader" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
                    <circle class="internal-circle" cx="60" cy="60" r="30"></circle>
                    <circle class="external-circle" cx="60" cy="60" r="50"></circle>
                </svg>

                <strong class="mt-2">
                    {{ 'checkout.steps.flights.lookingForFlights' | trans }}
                </strong>

                <!-- Get random text of loadingTexts array -->
                <small class="mt-1 text-muted">
                    <i class="far fa-square-quote mr-1"></i>
                    {{ loadingTexts[Math.floor(Math.random() * loadingTexts.length)] }}
                </small>
            </div>

            <!-- Results -->
            <div class="list-group">
                <!-- Error -->
                <div class="alert alert-danger mb-0 rounded-0 checkout-error-alert alert-dismissible fade show" v-if="isError">
                    <h4 class="alert-heading">
                        {{ 'checkout.errorHeading' | trans }}
                    </h4>

                    <p class="mb-0">
                        {{ 'checkout.steps.flights.errors.' + (errorType || 'default') | trans }}
                    </p>
                </div>

                <div class="form-check list-group-item flight-container"
                v-for="(flight, flightIndex) in flights"
                :class="{ active: selectedFlight && selectedFlight.token === flight.token }"
                :key="'flight_' + flight.token"
                v-show="selectedFlight === null || (selectedFlight && selectedFlight.token === flight.token)">
                    <input type="radio" :value="flight" v-model="selectedFlight" :id="'flight' + flight.token" />
                    <label :for="'flight' + flight.token">
                        <span class="inner-container">
                            <!-- Segment group -->
                            <span class="segment-group" v-for="(segmentGroup, segmentGroupIndex) in flight.segment_groups" :key="'segment_group_' + segmentGroupIndex">
                                <!-- Airline / Carrier logo -->
                                <img class="carrier-logo"
                                :src="$cdn.default + 'airlines/' + String(carrier).toLowerCase() + '.png'"
                                v-for="carrier in $lodash.uniq(segmentGroup.segments.map(segment => segment.carrier_code))"
                                :key="'carrier_' + carrier + '_' + flightIndex" />

                                <!-- Title -->
                                <span class="title">
                                    {{ 'checkout.steps.flights.segments.title.' + (segmentGroupIndex === 0 ? 'arrival' : (segmentGroupIndex === flight.segment_groups.length - 1 ? 'departure' : 'connection' )) | trans }}
                                </span>

                                <!-- Segments -->
                                <span class="segments">
                                    <!-- Single segment -->
                                    <span class="segment">
                                        <!-- Departure time -->
                                        <span class="time">
                                            {{ $moment(segmentGroup.segments[0].departure_at).format('HH:mm') }}
                                        </span>

                                        <!-- Airport -->
                                        <span class="iata-code">
                                            {{ segmentGroup.segments[0].origin }}
                                        </span>
                                    </span>

                                    <!-- Icon -->
                                    <i class="fas fa-plane"></i>

                                    <!-- Single segment -->
                                    <span class="segment">
                                        <!-- Arrival time -->
                                        <span class="time">
                                            {{ $moment(segmentGroup.segments[segmentGroup.segments.length - 1].arrival_at).format('HH:mm') }}
                                        </span>

                                        <!-- Airport -->
                                        <span class="iata-code">
                                            {{ segmentGroup.segments[segmentGroup.segments.length - 1].destination }}
                                        </span>
                                    </span>
                                </span>

                                <!-- Meta -->
                                <span class="meta">
                                    <!-- Duration -->
                                    <span class="duration">
                                        <!-- Days -->
                                        <span class="days" v-if="segmentGroup.duration.days > 0">
                                            {{ segmentGroup.duration.days }}
                                            {{ 'general.days' | choice(segmentGroup.duration.days) }}
                                        </span>

                                        <!-- Hours -->
                                        <span class="hours" v-if="segmentGroup.duration.hours > 0">
                                            {{ segmentGroup.duration.hours }}
                                            {{ 'general.hoursShort' | trans }}
                                        </span>

                                        <!-- Minutes -->
                                        <span class="minutes" v-if="segmentGroup.duration.minutes > 0">
                                            {{ segmentGroup.duration.minutes }}
                                            {{ 'general.minutesShort' | trans }}
                                        </span>
                                    </span>

                                    <!-- Stops descriptor -->
                                    <span class="stops-descriptor" :class="{ 'has-stops': segmentGroup.segments.length > 1 || segmentGroup.segments.filter(segment => segment.stops_count > 0).length > 0 }">
                                        <!-- With stops -->
                                        <span v-if="segmentGroup.segments.length > 1 || segmentGroup.segments.filter(segment => segment.stops_count > 0).length > 0">
                                            <i class="fas fa-info-circle"></i>
                                            {{ 'checkout.steps.flights.stopsDescriptor.stops' | choice(stopsDetails(segmentGroup).count, { count: stopsDetails(segmentGroup).count, codes: stopsDetails(segmentGroup).airports }) }}
                                        </span>

                                        <!-- Direct flight -->
                                        <span v-if="! (segmentGroup.segments.length > 1 || segmentGroup.segments.filter(segment => segment.stops_count > 0).length > 0)">
                                            {{ 'checkout.steps.flights.stopsDescriptor.direct' | trans }}
                                        </span>
                                    </span>

                                    <!-- Small bag only -->
                                    <span class="small-bag-only d-block" v-if="segmentGroup.segments.filter(segment => dictionary.airlines[segment.operator_code].regulations && dictionary.airlines[segment.operator_code].regulations.is_base_fare_small_bag_only === true).length > 0">
                                        <i class="far fa-suitcase-rolling mr-1"></i>
                                        {{ 'checkout.steps.flights.smallBagOnly' | trans }}
                                    </span>

                                    <!-- Checked bag included -->
                                    <span class="d-block text-primary" v-if="flight.has_checked_bags">
                                        <i class="far fa-suitcase mr-1"></i>
                                        {{ 'checkout.steps.flights.checkedBagsIncluded' | trans }}
                                        <i class="far fa-check ml-1"></i>
                                    </span>
                                </span>
                            </span>

                            <span class="side">
                                <!-- Price -->
                                <span class="price" v-if="flight.price">
                                    <span v-if="flight.id in checkedPrices">
                                        {{ checkedPrices[flight.id].checked_total | formatPrice }}
                                    </span>
                                    <span v-if="! (flight.id in checkedPrices)">
                                        {{ flight.price | formatPrice }}
                                    </span>
                                    &euro;
                                    <small>
                                        {{ 'checkout.perPerson' | trans }}
                                    </small>

                                    <!-- Checked -->
                                    <small class="text-success" v-if="flight.id in checkedPrices">
                                        <i class="far fa-check mr-1"></i>
                                        Bestätigt
                                    </small>
                                </span>

                                <!-- Details -->
                                <button type="button" class="btn btn-link" @click.prevent="showDetails(flight)">
                                    {{ 'checkout.steps.flights.details' | trans }}
                                </button>
                            </span>
                        </span>

                        <!-- Included bags -->
                        <!-- <span class="services-container" v-if="flight.additional_services && flight.additional_services.length > 0">
                            <span v-for="(service, serviceIndex) in flight.additional_services" :key="'additional_service_' + flight.id + '_' + serviceIndex">
                                <i class="fal fa-luggage-cart" v-if="service.type === 'checked_bag'"></i>
                                <i class="fal fa-utensils" v-if="service.type === 'meal'"></i>
                                <i class="fal fa-chair-office" v-if="service.type === 'seat'"></i>
                                <i class="fal fa-rabbit-fast" v-if="service.type === 'priority_boarding'"></i>
                                <i class="fal fa-check" v-if="service.type === 'other_service' || service.type === 'airport_checkin'"></i>

                                {{ 'search.fees.calculationTypes.inclusive_short' | trans }}
                                {{ 'flights.additional_service_type.' + service.type | trans }}
                            </span>
                        </span> -->

                        <!-- Additional services -->
                        <span class="services-container" v-if="flight.additional_services && flight.additional_services.length > 0">
                            <span v-for="(service, serviceIndex) in flight.additional_services" :key="'additional_service_' + flight.id + '_' + serviceIndex">
                                <i class="fal fa-luggage-cart" v-if="service.type === 'checked_bag'"></i>
                                <i class="fal fa-utensils" v-if="service.type === 'meal'"></i>
                                <i class="fal fa-chair-office" v-if="service.type === 'seat'"></i>
                                <i class="fal fa-rabbit-fast" v-if="service.type === 'priority_boarding'"></i>
                                <i class="fal fa-check" v-if="service.type === 'other_service' || service.type === 'airport_checkin'"></i>

                                {{ 'search.fees.calculationTypes.inclusive_short' | trans }}
                                {{ 'flights.additional_service_type.' + service.type | trans }}
                            </span>
                        </span>
                    </label>
                </div>

                <!-- Reset selected flight -->
                <button type="button" class="btn btn-link btn-lg py-3" style="height: auto;" @click.prevent="selectedFlight = null" v-if="selectedFlight">
                    <i class="far fa-pencil mr-1"></i>
                    {{ 'checkout.steps.flights.resetSelectedFlight' | trans }}
                </button>

                <!-- Checking flight loading animation -->
                <div class="d-flex flex-column py-3 justify-content-center align-items-center" v-if="isLoading === 'checkFlight'">
                    <svg class="loader" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
                        <circle class="internal-circle" cx="60" cy="60" r="30"></circle>
                        <circle class="external-circle" cx="60" cy="60" r="50"></circle>
                    </svg>

                    <strong class="mt-2">
                        {{ 'checkout.steps.flights.checkingYourFlight' | trans }}
                    </strong>

                    <!-- Get random text of loadingTexts array -->
                    <small class="mt-1 text-muted">
                        <i class="far fa-square-quote mr-1"></i>
                        {{ loadingTexts[Math.floor(Math.random() * loadingTexts.length)] }}
                    </small>
                </div>
            </div>
        </div>

        <!-- Notice for small bags -->
        <div class="small-bags-notice" v-if="isSelectedFlightWithSmallBags && !isLoading">
            <h3>
                {{ 'checkout.steps.flights.smallBagsNotice.heading' | trans() }}
            </h3>

            <p>
                {{ 'checkout.steps.flights.smallBagsNotice.paragraph_1' | trans() }}
            </p>
            <p>
                {{ 'checkout.steps.flights.smallBagsNotice.paragraph_2' | trans() }}
            </p>
            <p>
                {{ 'checkout.steps.flights.smallBagsNotice.paragraph_3' | trans() }}
            </p>

            <!-- Display fee details -->
            <span v-for="(feeDetails, feeDetailsIndex) in bagFeeWebsites" :key="'fee_details' + feeDetailsIndex">
                <a :href="feeDetails.url" target="_blank">
                    {{ feeDetails.airline.name }}
                    <i class="fa fa-external-link-alt ml-1" style="font-size: 12px;"></i>
                </a>

                <small class="d-block text-muted mt-2" v-if="feeDetails.updated_at && feeDetails.carry_on_bag_fee_from_amount && feeDetails.carry_on_bag_fee_to_amount">
                    <strong>{{ 'checkout.steps.flights.smallBagsNotice.feeInfoLabel' | trans({ date: $moment(feeDetails.updated_at).format('DD.MM.YYYY'), airline: feeDetails.airline.name }) }}</strong>
                    {{ 'checkout.steps.flights.smallBagsNotice.feeInfoText' | trans({ from: feeDetails.carry_on_bag_fee_from_amount + ' €', to: feeDetails.carry_on_bag_fee_to_amount + ' €' }) }}
                </small>
            </span>
        </div>

        <!-- Bags -->
        <h2 class="mt-4 mb-3" v-if="(selectedFlight && bagOptions && bagOptions.length) > 0">
            {{ 'checkout.steps.flights.addBags' | trans }}
        </h2>
        <div class="card-layout" v-if="selectedFlight && bagOptions && bagOptions.length > 0">
            <div class="list-group bag-options-list">
                <div class="form-check list-group-item flight-container" :class="{ active: selectedBagOption === null }">
                    <input type="radio" :value="null" v-model="selectedBagOption" :id="'bag_option_none'" />
                    <label :for="'bag_option_none'">
                        <span class="inner-container">
                            <span class="flex-grow-1">
                                Kein Zusatzgepäck
                            </span>

                            <span class="side">
                                <!-- Price -->
                                <span class="price">
                                    inklusive
                                    <small>
                                        {{ 'checkout.perPerson' | trans }}
                                    </small>
                                </span>
                            </span>
                        </span>
                    </label>
                </div>

                <div class="form-check list-group-item flight-container"
                v-for="(option, optionIndex) in bagOptions"
                :class="{ active: getBagOptionKey(selectedBagOption) === getBagOptionKey(option) }"
                :key="'bag_option_' + optionIndex">
                    <input type="radio" :value="option" v-model="selectedBagOption" :id="'bag_option_' + optionIndex" />
                    <label :for="'bag_option_' + optionIndex">
                        <span class="inner-container">
                            <span class="flex-grow-1">
                                {{ option.quantity }}x
                                {{ option.name }}
                                <span v-if="option.weight">
                                    ({{ option.weight }} {{ String(option.weight_unit).toLowerCase() }})
                                </span>
                            </span>

                            <span class="side">
                                <!-- Price -->
                                <span class="price" v-if="option.price">
                                    {{ option.price.amount | formatPrice }}
                                    &euro;
                                    <small>
                                        {{ 'checkout.perPerson' | trans }}
                                    </small>
                                </span>
                            </span>
                        </span>
                    </label>
                </div>
            </div>
        </div>

        <!-- Details modal -->
        <div class="modal fade flight-details-modal" id="modal-flight-details" tabindex="-1" role="dialog">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h4 class="modal-title">
                            Details
                        </h4>

                        <button type="button" class="close" data-dismiss="modal" aria-hidden="true">
                            &times;
                        </button>
                    </div>

                    <div class="modal-body" v-if="currentFlight">
                        <div class="flight-container">
                            <!-- Segment group -->
                            <span class="segment-group"
                            v-for="(segmentGroup, segmentGroupIndex) in currentFlight.segment_groups"
                            :key="'current_flight_segment_group_' + segmentGroupIndex"
                            :data-title="'checkout.steps.flights.segments.title.' + (segmentGroupIndex === 0 ? 'arrival' : (segmentGroupIndex === currentFlight.segment_groups.length - 1 ? 'departure' : 'connection' )) | trans">

                                <!-- Segments -->
                                <span class="segments"
                                v-for="(segment, segmentIndex) in segmentGroup.segments"
                                :key="'current_flight_segment_' + segmentGroupIndex + '_' + segmentIndex">
                                    <!-- Airline / Carrier logo -->
                                    <img class="carrier-logo"
                                    :src="$cdn.default + 'airlines/' + String(segment.carrier_code).toLowerCase() + '.png'" />

                                    <!-- Single segment -->
                                    <span class="segment">
                                        <!-- Airport -->
                                        <span class="iata-code">
                                            {{ segment.origin }}
                                        </span>

                                        <!-- Departure time -->
                                        <span class="time">
                                            {{ $moment(segment.departure_at).format('HH:mm') }}
                                        </span>
                                    </span>

                                    <!-- Icon -->
                                    <i class="fas fa-plane"></i>

                                    <!-- Single segment -->
                                    <span class="segment">
                                        <!-- Airport -->
                                        <span class="iata-code">
                                            {{ segment.destination }}
                                        </span>

                                        <!-- Arrival time -->
                                        <span class="time">
                                            {{ $moment(segment.arrival_at).format('HH:mm') }}
                                        </span>
                                    </span>
                                </span>

                                <!-- Meta -->
                                <span class="meta">
                                    <!-- Duration -->
                                    <span class="duration">
                                        <!-- Days -->
                                        <span class="days" v-if="segmentGroup.duration.days > 0">
                                            {{ segmentGroup.duration.days }}
                                            {{ 'general.days' | choice(segmentGroup.duration.days) }}
                                        </span>

                                        <!-- Hours -->
                                        <span class="hours" v-if="segmentGroup.duration.hours > 0">
                                            {{ segmentGroup.duration.hours }}
                                            {{ 'general.hoursShort' | trans }}
                                        </span>

                                        <!-- Minutes -->
                                        <span class="minutes" v-if="segmentGroup.duration.minutes > 0">
                                            {{ segmentGroup.duration.minutes }}
                                            {{ 'general.minutesShort' | trans }}
                                        </span>
                                    </span>

                                    <!-- Stops descriptor -->
                                    <span class="stops-descriptor" :class="{ 'has-stops': segmentGroup.segments.length > 1 || segmentGroup.segments.filter(segment => segment.stops_count > 0).length > 0 }">
                                        <!-- With stops -->
                                        <span v-if="segmentGroup.segments.length > 1 || segmentGroup.segments.filter(segment => segment.stops_count > 0).length > 0">
                                            <i class="fas fa-info-circle"></i>
                                            {{ 'checkout.steps.flights.stopsDescriptor.stops' | choice(stopsDetails(segmentGroup).count, { count: stopsDetails(segmentGroup).count, codes: stopsDetails(segmentGroup).airports }) }}
                                        </span>

                                        <!-- Direct flight -->
                                        <span v-if="! (segmentGroup.segments.length > 1 || segmentGroup.segments.filter(segment => segment.stops_count > 0).length > 0)">
                                            {{ 'checkout.steps.flights.stopsDescriptor.direct' | trans }}
                                        </span>
                                    </span>
                                </span>
                            </span>

                            <span class="side">
                                <!-- Price -->
                                <span class="price" v-if="currentFlight.price">
                                    {{ currentFlight.price | formatPrice }}
                                    &euro;
                                    <small>
                                        {{ 'checkout.perPerson' | trans }}
                                    </small>
                                </span>
                            </span>
                        </div>
                    </div>

                    <!-- Modal Actions -->
                    <div class="modal-footer">
                        <button type="button" class="btn btn-link" data-dismiss="modal">
                            Fenster schließen
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import Echo from 'laravel-echo';
import { flatten, uniqBy } from 'lodash';

    export default {
        props: ['initialOriginAirports', 'isLoading'],
        data() {
            return {
                Echo: null,
                loadingState: {
                    count: 0,
                    total: 0,
                },
                isError: false,
                errorType: null,
                data: null,
                selectedFlight: null,
                currentFlight: null,
                selectedBagOption: null,
                totalPriceChange: {
                    flight_token: null,
                    change: 0,
                },
                eventToken: null,
                upgrades: null,
				bagOptions: null,
				priceChanges: [],
                originAirports: [],
				destinationAirports: [],

                // Axios cancel sources
                searchCancelSource: null,
                upgradesCancelSource: null,
				checkCancelSource: null,

                // Filters
                possibleTimeFrames: ['morning', 'noon', 'evening', 'night'],
                filters: {
                    max_stops: null,
                    outbound_time_frame: null,
                    return_time_frame: null,
                    airlines: [],
                },
                loadingTexts: [
                    'In Finnland gibt es eine jährliche Weltmeisterschaft im Luftgitarrenspielen.',
                    'In Australien gibt es eine Straße namens "Nullarbor" die für 146,6 km absolut gerade verläuft.',
                    'Die höchste Erhebung der Niederlande ist ein 322,7 Meter hoher Hügel namens "Vaalse Berg".',
                    'In Japan gibt es ein Hotel, das von Robotern geleitet wird.',
                    'In Island gibt es Straßen, auf denen man nur mit Geländefahrzeugen fahren darf, um die empfindliche Natur zu schützen.',
                    'In Mexiko gibt es eine Halbinsel namens "Baja California", die größer ist als Italien.',
                    'In China gibt es eine Stadt, in der es keine Straßen gibt. Die Bewohner bewegen sich auf Schienen.',
                ],
            }
        },
        mounted() {
            this.setupEcho();
        },
        computed: {
            flights() {
                const flights = this.data !== null && typeof (this.data.flight_offers) !== 'undefined' ? this.data.flight_offers : []
                
                return this.$lodash.sortBy(flights, ['price'], ['asc'])
            },
            dictionary() {
                return this.data !== null && typeof (this.data.dictionary) !== 'undefined' ? this.data.dictionary : {
                    airlines: {},
                    airports: {},
                }
            },
            possibleAirlines() {
                if (this.data !== null && typeof (this.data.dictionary) !== 'undefined') {
                    return {
                        ...this.data.dictionary.airlines,
                    }
                }

                return {};
            },
            
            checkedPrices() {
                if (this.priceChanges) {
                    const groupedChanges = this.$lodash.groupBy(this.priceChanges, 'offer_id')
                    
                    Object.keys(groupedChanges).forEach((key) => {
                        groupedChanges[key] = groupedChanges[key][0]
                    });

                    return groupedChanges
                }

                return {}
            },
            isSelectedFlightWithSmallBags() {
                if (!this.selectedFlight) return false

                return this.selectedFlight.segment_groups.filter((segmentGroup) => {
                    return segmentGroup.segments.filter(segment => this.dictionary.airlines[segment.operator_code]?.regulations?.is_base_fare_small_bag_only === true).length > 0
                }).length > 0
            },
            bagFeeWebsites() {
                if (!this.selectedFlight) {
                    return []
                }

                // Get all segments with bag fee
                const segmentsWithBagFee = this.selectedFlight.segment_groups.map((segmentGroup) => {
                    return segmentGroup.segments.filter(segment => this.dictionary.airlines[segment.operator_code]?.regulations?.is_base_fare_small_bag_only === true && this.dictionary.airlines[segment.operator_code]?.regulations?.carry_on_bag_fees_website)
                })
                .filter((segments) => {
                    return segments.length > 0
                });

                // Flatten segments
                var dictionary = this.dictionary
                const feeDetails = flatten(segmentsWithBagFee)
                    .map((segment) => ({
                        airline: dictionary.airlines[segment.operator_code],
                        url: dictionary.airlines[segment.operator_code].regulations.carry_on_bag_fees_website,
                        carry_on_bag_fee_from_amount: dictionary.airlines[segment.operator_code].regulations.carry_on_bag_fee_from_amount,
                        carry_on_bag_fee_to_amount: dictionary.airlines[segment.operator_code].regulations.carry_on_bag_fee_to_amount,
                        updated_at: dictionary.airlines[segment.operator_code].regulations.carry_on_bag_fee_updated_at,
                    }));

                return uniqBy(feeDetails, 'website');
            },
        },
        methods: {
            setupEcho() {
				this.Echo = new Echo({
					broadcaster: 'pusher',
					key: 'ff63da2a97603dcefac6',
					cluster: 'eu',
					encrypted: true,
					disableStats: true,
				});

				var self = this;
				this.Echo.channel('checkout_channel.' + this.$parent.channelToken)
				.listen('.flights.offers', (event) => {
					self.handleEvent(event);
				})
			},
			handleEvent(event) {
				// Handle flights offers
				if (event.type === 'flights.offers' && (event.event_token === this.eventToken || this.eventToken === null)) {
					// Init if null
					if (this.data === null) {
						this.data = {
							dictionary: {
								airlines: {},
								airports: {},
							},
							flight_offers: [],
							loaded_chunks: [],
							chunks_count: {},
						};
					}

					// Merge dictionary
					if (event.data && event.data.dictionary) {
						this.data = {
							...this.data,
							dictionary: {
								airlines: {
									...this.data.dictionary.airlines,
									...event.data.dictionary.airlines,
								},
								airports: {
									...this.data.dictionary.airports,
									...event.data.dictionary.airports,
								},
							},
						}
					}

					// Merge flight offers
					if (event.data && event.data.flight_offers) {
						this.data = {
							...this.data,
							flight_offers: [
								...this.data.flight_offers,
								...event.data.flight_offers,
							],
						}
					}

					// Merge chunk_token
					if (event.chunk_token) {
						this.data['loaded_chunks'].push(event.chunk_token)
					}

					// Merge chunks_count
					if (event.chunks_count) {
						this.data['chunks_count'][event.chunk_token] = event.chunks_count
					}

                    // Calculate loading state
                    this.loadingState = {
                        count: this.data.loaded_chunks.length,
                        total: (event.total_chunks_count - Object.keys(this.data?.chunks_count || {}).length) + Object.values(this.data?.chunks_count || {}).reduce((a, b) => a + b, 0),
                    }
				}
			},

            stopsDetails(segmentGroup) {
                const stopSegments = [...segmentGroup.segments]
                stopSegments.pop() // Remove the final segment

                return {
                    count: segmentGroup.segments.length - 1,
                    airports: stopSegments.map(segment => segment.destination),
                }
            },

            showDetails(flight) {
                this.currentFlight = {...flight}

                $('#modal-flight-details').modal('show');
            },

            getBagOptionKey(bagOption) {
                if (!bagOption) return 'empty';

                const keyParts = [
                    bagOption.weight,
                    bagOption.weight_unit,
                    bagOption.price.amount,
                    bagOption.price.currency,
                    bagOption.quantity,
                    bagOption.segment_ids.join('_'),
                    bagOption.traveller_ids.join('_'),
                ];
                
                return keyParts.join('-');
            },

            // Load flights
			loadFlights() {
				// Cancel previous search
                if (this.searchCancelSource !== null) {
                    this.searchCancelSource.cancel()
                }

                // Create cancel token
                const CancelToken = axios.CancelToken
                this.searchCancelSource = CancelToken.source()

				var self = this;
				this.$parent.isLoading = 'loadFlights';

                // Reset states
				self.data = null;
                self.loadingState = {
                    count: 0,
                    total: 0,
                };
                self.selectedFlight = null;
                self.selectedBagOption = null;
                self.isError = false;
                self.$parent.isError = false;
                self.errorType = null;
                self.upgrades = [];
                self.priceChanges = [];

				axios.post(route('api.checkout.flights'), {
					channel_token: this.$parent.channelToken,
					trip_id: this.$parent.tripId,
					persons: this.$parent.buildPersons(),
					arrive_date: this.$parent.arriveDate !== null ? this.$parent.arriveDate.format('YYYY-MM-DD') : null,
					departure_date: this.$parent.departureDate !== null ? this.$parent.departureDate.format('YYYY-MM-DD') : null,
					activity_id: this.$parent.activityID,
					activity_category_id: this.$parent.activityCategoryID,
					activity_date: this.$parent.activityDate,
					activity_time: this.$parent.activityTime,
					accommodation_id: this.$parent.accommodationID,
					accommodation_room_id: this.$parent.accommodationRoomID,
					origin_airports: this.initialOriginAirports.map(airport => airport.iata_airport_code),
					...this.filters,
				}, {
					cancelToken: this.searchCancelSource.token,
				}).then(function (response) {
					self.originAirports = response.data.data.origin_airports;
					self.destinationAirports = response.data.data.destination_airports;

					self.eventToken = response.data.data.event_token;

					self.$parent.isLoading = false;
				}).catch(function (error) {
					console.log(error);
					self.isError = true;
                    self.$parent.isError = true;
					self.$parent.isLoading = false;
				});

                // Automatically finish loading state after 15 seconds
                setTimeout(() => {
                    if (self.$parent.isLoading === 'loadFlights' && self.loadingState.count < self.loadingState.total) {
                        self.$parent.isLoading = false;
                        self.loadingState.count = self.loadingState.total;
                    }
                }, 15000);
			},

            // Load upgrades
            loadUpgrades() {
                const flight = this.selectedFlight || null

                // Remember selected flight
				this.$parent.selectedFlight = flight;

                // Prevent from resets
				if (flight === null) {
					return;
				}

                // Cancel previous search
                if (this.upgradesCancelSource !== null) {
                    this.upgradesCancelSource.cancel()
                }

                // Create cancel token
                const CancelToken = axios.CancelToken
                this.upgradesCancelSource = CancelToken.source()

                var self = this;
                this.$parent.isLoading = 'loadUpgrades';
                self.errorType = null;
                self.isError = false;
                this.upgrades = null;

                axios.post(route('api.checkout.flightUpgrades'), {
					channel_token: this.$parent.channelToken,
					trip_id: this.$parent.tripId,
					persons: this.$parent.buildPersons(),
					arrive_date: this.$parent.arriveDate !== null ? this.$parent.arriveDate.format('YYYY-MM-DD') : null,
					departure_date: this.$parent.departureDate !== null ? this.$parent.departureDate.format('YYYY-MM-DD') : null,
					activity_id: this.$parent.activityID,
					activity_category_id: this.$parent.activityCategoryID,
					activity_date: this.$parent.activityDate,
					activity_time: this.$parent.activityTime,
					accommodation_id: this.$parent.accommodationID,
					accommodation_room_id: this.$parent.accommodationRoomID,
					flight_token: flight.token,
				}, {
					cancelToken: this.upgradesCancelSource.token,
				}).then(function (response) {
					self.upgrades = response.data.data;

					// Update selectedFlight with first upgrade offer
					self.$parent.selectedFlight = response.data.data[0] || null;

					self.$parent.isLoading = false;
				}).catch(function (error) {
					console.log(error);
					self.isError = true;
					self.$parent.isLoading = false;
				});
            },

            // Check flight
            checkFlight() {
				const flight = this.selectedFlight || null
				const bagOption = this.selectedBagOption || null

				// Remember selected flight
				this.$parent.selectedFlight = flight;

				// Prevent from resets
				if (flight === null) {
					return;
				}

				// Cancel previous search
                if (this.checkCancelSource !== null) {
                    this.checkCancelSource.cancel()
                }

                // Create cancel token
                const CancelToken = axios.CancelToken
                this.checkCancelSource = CancelToken.source()

				var self = this;
				this.$parent.isLoading = 'checkFlight';
                self.$parent.isError = false;
                self.isError = false;
                self.errorType = null;
				this.priceChanges = []

				axios.post(route('api.checkout.checkFlight'), {
					channel_token: this.$parent.channelToken,
					trip_id: this.$parent.tripId,
					persons: this.$parent.buildPersons(),
					arrive_date: this.$parent.arriveDate !== null ? this.$parent.arriveDate.format('YYYY-MM-DD') : null,
					departure_date: this.$parent.departureDate !== null ? this.$parent.departureDate.format('YYYY-MM-DD') : null,
					activity_id: this.$parent.activityID,
					activity_category_id: this.$parent.activityCategoryID,
					activity_date: this.$parent.activityDate,
					activity_time: this.$parent.activityTime,
					accommodation_id: this.$parent.accommodationID,
					accommodation_room_id: this.$parent.accommodationRoomID,
					flight_token: flight.token,
					flight_bag_option: bagOption,
				}, {
					cancelToken: this.checkCancelSource.token,
				}).then(function (response) {
					// Update bag options + price changes
					self.bagOptions = response.data.data?.bag_options
					self.priceChanges = response.data.data?.price_changes

					// Update selectedFlight with checked flight
					self.$parent.selectedFlight = response.data.data?.offers[0] || null;

					self.$parent.isLoading = false;
				}).catch(function (error) {
					console.log(error);
                    self.bagOptions = null
					self.isError = true;
                    self.$parent.isError = true;
                    self.errorType = 'checkFlight';
					self.$parent.isLoading = false;
				});
			},
        },
        watch: {
            filters: {
                handler(val) {
                    this.loadFlights()
				},
				deep: true,
            },
            selectedFlight() {
                // this.loadUpgrades();
                this.bagOptions = null
                this.checkFlight()
            },
            selectedBagOption() {
                this.checkFlight()
            },

            priceChanges() {
                // Skip if it's the same flight
                if (this.totalPriceChange.flight_token === this.currentFlight?.token) {
                    return
                }

                if (this.priceChanges && this.selectedFlight?.token) {
                    this.totalPriceChange = {
                        flight_token: this.selectedFlight.token,
                        change: this.priceChanges.map(change => change.difference).reduce((a, b) => a + b, 0)
                    };

                    return;
                }

                this.totalPriceChange = {
                    flight_token: null,
                    change: 0,
                }
            },
        }
    }
</script>
